import app from 'app';
import _ from 'lodash-extended';
import { ref, onValue, update, off } from 'firebase/database';

app.namespace.Model.RemoteStash = function (
  options = {
    type: null,
    id: null,
    autosave: false,
  }
) {
  this.whenReady = new _.ColdPromise();
  this.type = options.type;
  this.id = options.id;
  this.fbPath = null;
  this.consolePrefix = `app.namespace.Model.RemoteStash (${this.type})::`;

  if (!_.isString(options.type) || _.isNull(options.id)) {
    app.doctorController.report(
      `${this.consolePrefix} Missing 'type' or 'id' properties during initialization!`
    );
    return;
  }

  if (this.type === 'userId') {
    this.fbPath = `users/${this.id}`;
  } else if (this.type === 'storyId') {
    this.fbPath = `stories/${this.id}`;
  } else {
    app.doctorController.report(
      `${this.consolePrefix} cannot determine path for type ${this.type}`
    );
  }

  Backbone.Model.apply(this, [{}]);

  if (options.autosave) {
    this.listenTo(this, 'change', this.save.bind(this));
  }
};

// Methods:
_.extend(app.namespace.Model.RemoteStash.prototype, Backbone.Model.prototype, {
  READONLY_FIELDS: ['multilogin-studio', 'multilogin-shortcut'],

  fetch() {
    app.api.feStash
      .authenticate({
        type: this.type,
        id: this.id,
      })
      .then(
        ({ fbdb, userId }) => {
          const resourceRef = ref(fbdb, this.fbPath);

          // fetch all data from firebase path
          onValue(
            resourceRef,
            (data) => {
              const val = data.val();
              if (_.isObject(val)) {
                this.attributes = val;
              }
              off(resourceRef);
              this.whenReady.resolve({ fbdb, userId, resourceRef });
            },
            () => {
              this.whenReady.reject();
              console.warn(
                `${this.consolePrefix} fetch - could not pull path ${this.fbPath}`
              );
            }
          );
        },
        (error) => {
          this.whenReady.reject();
          app.doctorController.report(
            `app.api.feStash[${this.type}]: auth fail`,
            null,
            error
          );
        }
      );
  },

  /**
   * Override model set function to wait for remote data fetch
   */
  set(attributes, options) {
    this.whenReady.finally(() => {
      Backbone.Model.prototype.set.call(this, attributes, options);
    });
  },

  /**
   * Override model unset function to wait for remote data fetch
   */
  unset(attributes) {
    this.whenReady.finally(() => {
      Backbone.Model.prototype.set.call(this, attributes, void 0, {
        unset: true,
      });
    });
  },

  /**
   * Protect clear function from unwanted scenarios using force option
   */
  clear(options = { force: false }) {
    if (!options.force) {
      console.warn(
        `${this.consolePrefix} clear method protected in remote stash, use {force:true} to force the flush`
      );
      return false;
    } else {
      this.whenReady.finally(() => {
        Backbone.Model.prototype.clear.call(this);
      });
    }
  },

  /**
   * save model to named remote stash firebase
   */
  save() {
    this.whenReady.then(({ resourceRef }) => {
      const obj = this._parseFirebasePayload(this.changed);
      if (!_.isEmpty(obj)) {
        update(resourceRef, obj).catch((err) => {
          app.doctorController.report(`${this.consolePrefix} save failed`, err);
        });
      }
    });
  },

  _parseFirebasePayload(obj) {
    obj = _.omit(obj, this.READONLY_FIELDS);
    for (const key in obj) {
      if (Object.getOwnPropertyDescriptor(obj, key)) {
        if (obj[key] === undefined) {
          obj[key] = null;
        }
      }
    }
    return obj;
  },
});

app.namespace.Model.RemoteStash.extend = Backbone.Model.extend;
export default app.namespace.Model.RemoteStash;
